import { BoundOperator, type QualityFormulaType, type SpecType } from '@local/lib';
import { ActionIcon, Checkbox, Group, NumberInput, SegmentedControl, Table, Text, Tooltip } from '@mantine/core';
import { Infinity as InfinityIcon } from 'lucide-react';
import { useState } from 'react';

import type { RangeFormulaRow } from '../qualityFormulaTypes';

import { FieldHelpTooltip } from '../../../components/FieldHelpTooltip';
import { toNumberOrDefault } from '../numberInputValue';
import {
  ADJUSTMENT_MODE_COLOR,
  ADJUSTMENT_MODES,
  AMOUNT_CONFIG_BY_FORMULA_TYPE,
  FIELD_HELP,
} from '../qualityFormulaMetadata';
import { getRangeRowExpressionLabel, validateRangeRow } from '../qualityFormulaValidation';
import { AnimatedItem } from './AnimatedItem';
import { TABLE_HEADER_STYLE } from './tableStyles';

interface RangeRowsTableProps {
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  rows: RangeFormulaRow[];
  specType: SpecType;
}

const ADJUSTMENT_MODE_OPTIONS = ADJUSTMENT_MODES.map(mode => ({ label: mode.label, value: mode.value }));
const LOWER_OPERATOR_OPTIONS = [
  { label: '>', value: BoundOperator.Exclusive },
  { label: '≥', value: BoundOperator.Inclusive },
];
const UPPER_OPERATOR_OPTIONS = [
  { label: '<', value: BoundOperator.Exclusive },
  { label: '≤', value: BoundOperator.Inclusive },
];

const TH_STYLE: React.CSSProperties = { ...TABLE_HEADER_STYLE, padding: '8px 8px' };

const TD_STYLE: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'middle' };

// Compact "does the boundary itself count?" toggle — real legacy formulas mix inclusive and
// exclusive per row (see getRangeBoundaryIssues), so it's exposed here rather than assumed fixed.
const BoundOperatorControl: React.FC<{
  data: Array<{ label: string; value: string }>;
  disabled?: boolean;
  label: string;
  onChange: (value: string) => void;
  value: string;
}> = ({ data, disabled, label, onChange, value }) => (
  <Tooltip label="Does this bound include the boundary value itself?" position="top" withArrow>
    <SegmentedControl aria-label={label} data={data} disabled={disabled} onChange={onChange} size="xs" value={value} />
  </Tooltip>
);

// Renders every Range row (PERCENTAGE / PER_UNIT formula types, RANGE tier mode) as one compact
// table — one row per range, columns for every configurable field — instead of a stacked card
// per range. Every bound, and its inclusive/exclusive operator, is independently editable on every
// row — a new row's lower bound only *defaults* to the previous row's upper bound (see
// handleAddRow) as a starting point, it's never locked to it; getRangeBoundaryIssues surfaces any
// resulting gap or overlap instead of preventing the edit.
export const RangeRowsTable: React.FC<RangeRowsTableProps> = ({
  formulaType,
  onRemoveRow,
  onRowChange,
  rows,
  specType,
}) => {
  const amountConfig = AMOUNT_CONFIG_BY_FORMULA_TYPE[formulaType];
  const allRowsFlat = rows.every(row => row.isFlatAdjustment);
  const someRowsFlat = rows.some(row => row.isFlatAdjustment);
  // Rows pending their exit animation — still present in `rows` (nothing's removed from the store
  // yet) but faded via AnimatedItem's `visible` prop until the transition finishes and
  // `finalizeRemoveRow` actually calls `onRemoveRow`.
  const [removingRowIds, setRemovingRowIds] = useState<Set<string>>(() => new Set());

  const handleToggleAllFlatAdjustment = (checked: boolean) => {
    rows.forEach((_row, rowIndex) => {
      onRowChange(specType, rowIndex, 'isFlatAdjustment', checked);
    });
  };

  const handleRemoveRow = (rowId: string) => {
    setRemovingRowIds(previous => new Set(previous).add(rowId));
  };

  // Re-looks up the row's current index (rather than capturing it at click time) so an in-flight
  // removal still targets the right row even if another row above it finished removing first.
  const finalizeRemoveRow = (rowId: string) => {
    const currentIndex = rows.findIndex(row => row.id === rowId);
    if (currentIndex !== -1) onRemoveRow(specType, currentIndex);
    setRemovingRowIds(previous => {
      const next = new Set(previous);
      next.delete(rowId);
      return next;
    });
  };

  return (
    <Table.ScrollContainer minWidth={720}>
      <Table horizontalSpacing="xs" verticalSpacing={4}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={TH_STYLE} />
            <Table.Th style={TH_STYLE}>Adjustment</Table.Th>
            <Table.Th style={TH_STYLE}>
              <Group gap={4} wrap="nowrap">
                Lower bound
                <FieldHelpTooltip label="The floor of this range. Defaults to the previous range's upper bound when added, but can be changed." />
              </Group>
            </Table.Th>
            <Table.Th style={TH_STYLE}>Upper bound</Table.Th>
            <Table.Th style={TH_STYLE}>{amountConfig.label}</Table.Th>
            <Table.Th style={TH_STYLE}>Per increment</Table.Th>
            <Table.Th style={TH_STYLE}>
              <Group gap={4} justify="center" wrap="nowrap">
                Flat adjustment
                <Checkbox
                  aria-label="Toggle flat adjustment for all rows"
                  checked={allRowsFlat}
                  indeterminate={someRowsFlat && !allRowsFlat}
                  onChange={event => {
                    handleToggleAllFlatAdjustment(event.currentTarget.checked);
                  }}
                />
                <FieldHelpTooltip label={FIELD_HELP.flatAdjustment} />
              </Group>
            </Table.Th>
            {/* Fixed width, reserved whether or not any row can currently show its remove button
                (only rows.length > 2 can) — otherwise the column collapses to ~0 at 1-2 rows and
                every other column visibly shifts left the moment a 3rd row makes it appear. */}
            <Table.Th style={{ ...TH_STYLE, width: 36 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, rowIndex) => {
            const isLastRow = rowIndex === rows.length - 1;
            const errors = validateRangeRow(row, formulaType);

            return (
              <AnimatedItem
                key={row.id}
                onExited={() => {
                  finalizeRemoveRow(row.id);
                }}
                visible={!removingRowIds.has(row.id)}
              >
                {styles => (
                  <Table.Tr
                    style={{
                      ...styles,
                      borderLeft: `3px solid var(--mantine-color-${ADJUSTMENT_MODE_COLOR[row.adjustmentMode]}-filled)`,
                    }}
                  >
                    <Table.Td style={TD_STYLE}>
                      <Tooltip label={getRangeRowExpressionLabel(row)} position="right" withArrow>
                        <Text c="dimmed" fw={700} fz="xs" style={{ cursor: 'help' }}>
                          {rowIndex + 1}
                        </Text>
                      </Tooltip>
                    </Table.Td>

                    <Table.Td style={TD_STYLE}>
                      <SegmentedControl
                        color={ADJUSTMENT_MODE_COLOR[row.adjustmentMode]}
                        data={ADJUSTMENT_MODE_OPTIONS}
                        onChange={value => {
                          onRowChange(specType, rowIndex, 'adjustmentMode', value);
                        }}
                        size="xs"
                        value={row.adjustmentMode}
                      />
                    </Table.Td>

                    <Table.Td style={TD_STYLE}>
                      <Group gap={4} wrap="nowrap">
                        <BoundOperatorControl
                          data={LOWER_OPERATOR_OPTIONS}
                          label="Lower bound operator"
                          onChange={value => {
                            onRowChange(specType, rowIndex, 'lowerBoundOperator', value);
                          }}
                          value={row.lowerBoundOperator}
                        />
                        <NumberInput
                          hideControls
                          onChange={value => {
                            onRowChange(specType, rowIndex, 'lowerBound', toNumberOrDefault(value, 0));
                          }}
                          styles={{ input: { width: 68 } }}
                          value={row.lowerBound}
                        />
                      </Group>
                    </Table.Td>

                    <Table.Td style={TD_STYLE}>
                      <Group gap={4} wrap="nowrap">
                        <NumberInput
                          disabled={row.upperBound === null}
                          error={row.upperBound !== null && errors.upperBound !== undefined}
                          hideControls
                          onChange={value => {
                            onRowChange(specType, rowIndex, 'upperBound', toNumberOrDefault(value, 0));
                          }}
                          styles={{ input: { width: 68 } }}
                          value={row.upperBound ?? ''}
                        />
                        <BoundOperatorControl
                          data={UPPER_OPERATOR_OPTIONS}
                          disabled={row.upperBound === null}
                          label="Upper bound operator"
                          onChange={value => {
                            onRowChange(specType, rowIndex, 'upperBoundOperator', value);
                          }}
                          value={row.upperBoundOperator}
                        />
                        {isLastRow ? (
                          <Tooltip label={row.upperBound === null ? 'Has no ceiling' : 'Set no upper limit'} withArrow>
                            <ActionIcon
                              aria-label="Toggle no upper limit"
                              color={row.upperBound === null ? 'blue' : 'gray'}
                              onClick={() => {
                                onRowChange(specType, rowIndex, 'upperBound', row.upperBound === null ? 0 : null);
                              }}
                              size="sm"
                              variant={row.upperBound === null ? 'filled' : 'subtle'}
                            >
                              <InfinityIcon size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                      </Group>
                    </Table.Td>

                    <Table.Td style={TD_STYLE}>
                      <NumberInput
                        error={errors.amount !== undefined}
                        hideControls
                        onChange={value => {
                          onRowChange(specType, rowIndex, amountConfig.field, toNumberOrDefault(value, 0));
                        }}
                        styles={{ input: { width: 80 } }}
                        value={row[amountConfig.field]}
                      />
                    </Table.Td>

                    <Table.Td style={TD_STYLE}>
                      <NumberInput
                        disabled={row.isFlatAdjustment}
                        error={!row.isFlatAdjustment && errors.perIncrement !== undefined}
                        hideControls
                        min={0}
                        onChange={value => {
                          onRowChange(specType, rowIndex, 'perIncrement', toNumberOrDefault(value, 0));
                        }}
                        placeholder={row.isFlatAdjustment ? 'N/A' : undefined}
                        styles={{ input: { width: 68 } }}
                        value={row.isFlatAdjustment ? '' : row.perIncrement}
                      />
                    </Table.Td>

                    <Table.Td style={TD_STYLE}>
                      <Checkbox
                        aria-label="Flat adjustment"
                        checked={row.isFlatAdjustment}
                        onChange={event => {
                          onRowChange(specType, rowIndex, 'isFlatAdjustment', event.currentTarget.checked);
                        }}
                        styles={{ root: { display: 'flex', justifyContent: 'center' } }}
                      />
                    </Table.Td>

                    <Table.Td style={TD_STYLE}>
                      {rows.length > 2 ? (
                        <ActionIcon
                          aria-label={`Remove range ${rowIndex + 1}`}
                          color="red"
                          onClick={() => {
                            handleRemoveRow(row.id);
                          }}
                          size="sm"
                          variant="subtle"
                        >
                          ×
                        </ActionIcon>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                )}
              </AnimatedItem>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};
